import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  BRUSH_SIZE_MAX,
  BRUSH_SIZE_MIN,
  BRUSH_TYPES,
  drawStroke,
  type BrushType
} from '@renderer/lib/stickers/brush'

/** One row of ready colours; the last swatch opens any colour. */
export const BRUSH_COLORS = [
  '#111111',
  '#FFFFFF',
  '#E53935',
  '#FB8C00',
  '#FDD835',
  '#43A047',
  '#00ACC1',
  '#1E88E5',
  '#8E24AA',
  '#EC407A',
  '#8D6E63'
]

/** A short wave in the brush's own style, drawn the way the strip will draw it. */
const BrushSample: React.FC<{ type: BrushType; color: string }> = ({ type, color }) => {
  const ref = useRef<HTMLCanvasElement>(null)
  useEffect(() => {
    const canvas = ref.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    const ratio = window.devicePixelRatio || 1
    canvas.width = 96 * ratio
    canvas.height = 36 * ratio
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    // A pale colour on a pale tile would vanish; show the sample in ink then.
    const ink = /^#f/i.test(color) ? '#111111' : color
    const points = Array.from({ length: 13 }, (_, i) => ({
      xPct: 10 + i * 6.67,
      yPct: 50 + Math.sin((i / 12) * Math.PI * 2) * 22
    }))
    drawStroke(ctx, { id: 'sample', points, color: ink, sizePct: 6, brushType: type }, canvas.width, canvas.height)
  }, [type, color])
  return <canvas ref={ref} className="h-9 w-24" aria-hidden />
}

/**
 * The Draw tab: the classic editor's brush — all six brushes, any colour, the
 * same 1–50 size range — laid out as one column of plain controls.
 */
const BrushPanel: React.FC<{
  type: BrushType
  color: string
  size: number
  primary: string
  onType: (type: BrushType) => void
  onColor: (color: string) => void
  onSize: (size: number) => void
}> = ({ type, color, size, primary, onType, onColor, onSize }) => {
  const { t } = useTranslation()
  const custom = !BRUSH_COLORS.some((c) => c.toLowerCase() === color.toLowerCase())
  const sectionLabel = 'mb-3 text-base font-semibold text-carbon/70'

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-1">
      <section>
        <h2 className={sectionLabel}>{t('stickers.brush', 'Brush')}</h2>
        <div className="grid grid-cols-3 gap-3">
          {BRUSH_TYPES.map((key) => {
            const on = key === type
            return (
              <button
                key={key}
                type="button"
                aria-pressed={on}
                onClick={() => onType(key)}
                className="flex flex-col items-center gap-1 rounded-xl border-2 bg-white px-2 py-3 transition-all active:scale-95"
                style={
                  on
                    ? { borderColor: primary, background: `${primary}12` }
                    : { borderColor: '#eef2f7' }
                }
              >
                <BrushSample type={key} color={color} />
                <span className="text-sm font-medium text-carbon">
                  {t(`stickers.brushTypes.${key}`, key)}
                </span>
              </button>
            )
          })}
        </div>
      </section>

      <section>
        <h2 className={sectionLabel}>{t('stickers.color', 'Colour')}</h2>
        <div className="flex flex-wrap gap-3">
          {BRUSH_COLORS.map((c) => {
            const on = c.toLowerCase() === color.toLowerCase()
            return (
              <button
                key={c}
                type="button"
                aria-label={c}
                aria-pressed={on}
                onClick={() => onColor(c)}
                className="h-11 w-11 rounded-full border border-slate-300 transition-transform active:scale-90"
                style={{
                  background: c,
                  boxShadow: on ? `0 0 0 3px #fff, 0 0 0 6px ${primary}` : undefined
                }}
              />
            )
          })}
          <label
            className="relative h-11 w-11 cursor-pointer overflow-hidden rounded-full border border-slate-300"
            style={{
              background: custom
                ? color
                : 'conic-gradient(#e53935, #fdd835, #43a047, #00acc1, #1e88e5, #8e24aa, #e53935)',
              boxShadow: custom ? `0 0 0 3px #fff, 0 0 0 6px ${primary}` : undefined
            }}
            aria-label={t('stickers.customColor', 'Any colour')}
          >
            <input
              type="color"
              value={color}
              onChange={(e) => onColor(e.target.value)}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            />
          </label>
        </div>
      </section>

      <section>
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="text-base font-semibold text-carbon/70">{t('stickers.size', 'Size')}</h2>
          <span className="font-mono text-base tabular-nums text-carbon">{size}</span>
        </div>
        <div className="flex items-center gap-4">
          <span
            className="shrink-0 rounded-full bg-carbon"
            style={{ width: 6, height: 6 }}
            aria-hidden
          />
          <input
            type="range"
            min={BRUSH_SIZE_MIN}
            max={BRUSH_SIZE_MAX}
            value={size}
            onChange={(e) => onSize(parseInt(e.target.value, 10))}
            className="h-2 w-full cursor-pointer"
            style={{ accentColor: primary }}
            aria-label={t('stickers.size', 'Size')}
          />
          <span
            className="shrink-0 rounded-full bg-carbon"
            style={{ width: 22, height: 22 }}
            aria-hidden
          />
        </div>
      </section>
    </div>
  )
}

export default BrushPanel
